import { NodeType, EdgeType } from '../../../types/graph.types';

/**
 * Cytoscape Configuration
 * 
 * Defines visual styles for all node and edge types in the knowledge graph.
 * Includes styles for future todo nodes and task relationships.
 */

// Color palette for node types
export const nodeColors = {
  [NodeType.MESSAGE]: '#4056A6',      // Royal Blue
  [NodeType.DOCUMENT]: '#325BA8',     // Deep Blue
  [NodeType.CONCEPT]: '#4DC4D2',      // Turquoise
  [NodeType.PERSON]: '#892987',       // Purple
  [NodeType.TOPIC]: '#E94257',        // Coral Red
  [NodeType.ORGANIZATION]: '#999999',  // Gray
  email: '#FF9800',                   // Orange (for email nodes)
  knowledge: '#4DC4D2',                // Turquoise (for knowledge nodes)
  conversation: '#325BA8',             // Deep Blue (for conversation nodes)
  // Future todo colors
  // [NodeType.TODO]: '#FF6B6B',       // Light Red
  // [NodeType.TASK]: '#4ECDC4',       // Teal
  // [NodeType.PROJECT]: '#45B7D1',    // Sky Blue
};

// Edge colors by type
export const edgeColors = {
  [EdgeType.REFERENCES]: '#666666',
  [EdgeType.MENTIONS]: '#888888',
  [EdgeType.REPLIES_TO]: '#4056A6',
  [EdgeType.RELATED_TO]: '#999999',
  [EdgeType.TAGGED_WITH]: '#E94257',
  [EdgeType.BELONGS_TO]: '#325BA8',
  [EdgeType.CREATED_BY]: '#892987',
  // Semantic relationship edge colors
  [EdgeType.SUPPORTS]: '#4DC4D2',     // Turquoise
  [EdgeType.CONTRADICTS]: '#E94257',   // Coral Red
  [EdgeType.CAUSES]: '#325BA8',        // Deep Blue
  [EdgeType.DEPENDS_ON]: '#892987',    // Purple
};

// Node type specific styles
export const nodeTypeStyles = {
  message: {
    shape: 'ellipse',
    width: 60,
    height: 60,
  },
  document: {
    shape: 'rectangle',
    width: 80,
    height: 60,
  },
  concept: {
    shape: 'diamond',
    width: 70,
    height: 70,
  },
  person: {
    shape: 'round-rectangle',
    width: 90,
    height: 40,
  },
  topic: {
    shape: 'hexagon',
    width: 70,
    height: 70,
  },
  organization: {
    shape: 'octagon',
    width: 80,
    height: 80,
  },
  email: {
    shape: 'round-rectangle',
    width: 100,
    height: 45,
    borderStyle: 'solid',
    borderWidth: 2,
  },
  knowledge: {
    shape: 'diamond',
    width: 80,
    height: 80,
    borderStyle: 'solid',
  },
  conversation: {
    shape: 'round-octagon',
    width: 90,
    height: 70,
    borderStyle: 'solid',
    borderWidth: 3,
  },
  // Future todo styles
  todo: {
    shape: 'round-rectangle',
    width: 100,
    height: 50,
    borderStyle: 'dashed',
  },
  task: {
    shape: 'round-rectangle',
    width: 90,
    height: 45,
    borderStyle: 'solid',
  },
  project: {
    shape: 'round-hexagon',
    width: 100,
    height: 60,
    borderStyle: 'double',
  },
};

// Edge type specific styles
export const edgeTypeStyles = {
  references: {
    lineStyle: 'solid',
    width: 1,
    targetArrowShape: 'triangle',
  },
  mentions: {
    lineStyle: 'dashed',
    width: 1,
    targetArrowShape: 'circle',
  },
  replies_to: {
    lineStyle: 'solid',
    width: 2,
    targetArrowShape: 'triangle',
  },
  related_to: {
    lineStyle: 'dotted',
    width: 1,
    targetArrowShape: 'none',
  },
  tagged_with: {
    lineStyle: 'solid',
    width: 1,
    targetArrowShape: 'triangle-tee',
  },
  belongs_to: {
    lineStyle: 'solid',
    width: 2,
    targetArrowShape: 'triangle',
  },
  created_by: {
    lineStyle: 'solid',
    width: 1,
    targetArrowShape: 'triangle',
  },
  // Future todo edge styles
  blocks: {
    lineStyle: 'solid',
    width: 3,
    targetArrowShape: 'triangle-cross',
    lineColor: '#FF6B6B',
  },
  subtask_of: {
    lineStyle: 'solid',
    width: 2,
    targetArrowShape: 'triangle',
    lineColor: '#4ECDC4',
  },
  assigned_to: {
    lineStyle: 'dashed',
    width: 2,
    targetArrowShape: 'circle',
    lineColor: '#892987',
  },
  // Semantic relationship edge styles
  supports: {
    lineStyle: 'solid',
    width: 2,
    targetArrowShape: 'triangle',
    lineColor: '#4DC4D2',
  },
  contradicts: {
    lineStyle: 'dashed',
    width: 2,
    targetArrowShape: 'triangle',
    lineColor: '#E94257',
  },
  causes: {
    lineStyle: 'solid',
    width: 2,
    targetArrowShape: 'triangle',
    lineColor: '#325BA8',
  },
  depends_on: {
    lineStyle: 'dotted',
    width: 2,
    targetArrowShape: 'triangle',
    lineColor: '#892987',
  },
};

// Main Cytoscape configuration
export const cytoscapeConfig = {
  style: [
    // Base node style
    {
      selector: 'node',
      style: {
        'label': 'data(label)',
        'text-valign': 'center',
        'text-halign': 'center',
        'text-wrap': 'wrap',
        'text-max-width': '100px',
        'font-size': '12px',
        'font-family': 'Roboto, sans-serif',
        'border-width': 2,
        'border-color': '#333',
        'background-opacity': 0.9,
        'overlay-padding': '6px',
        'z-index': 10,
      },
    },

    // Base edge style
    {
      selector: 'edge',
      style: {
        'width': 2,
        'line-color': '#999',
        'target-arrow-color': '#999',
        'target-arrow-shape': 'triangle',
        'curve-style': 'bezier',
        'label': 'data(label)',
        'font-size': '10px',
        'text-rotation': 'autorotate',
        'text-margin-y': -10,
        'text-background-color': '#ffffff',
        'text-background-opacity': 0.8,
        'text-background-padding': '2px',
        'arrow-scale': 1,
        'source-distance-from-node': 5,
        'target-distance-from-node': 5,
      },
    },

    // Node type specific styles
    ...Object.entries(NodeType).map(([key, value]) => ({
      selector: `node[type="${value}"]`,
      style: {
        'background-color': nodeColors[value] || '#666',
        'shape': nodeTypeStyles[value]?.shape || 'ellipse',
        'width': nodeTypeStyles[value]?.width || 60,
        'height': nodeTypeStyles[value]?.height || 60,
        'border-style': (nodeTypeStyles[value] as any)?.borderStyle || 'solid',
      },
    })),

    // Edge type specific styles
    ...Object.entries(EdgeType).map(([key, value]) => ({
      selector: `edge[type="${value}"]`,
      style: {
        'line-style': edgeTypeStyles[value]?.lineStyle || 'solid',
        'width': edgeTypeStyles[value]?.width || 1,
        'target-arrow-shape': edgeTypeStyles[value]?.targetArrowShape || 'triangle',
        'line-color': (edgeTypeStyles[value] as any)?.lineColor || edgeColors[value] || '#999',
        'target-arrow-color': (edgeTypeStyles[value] as any)?.lineColor || edgeColors[value] || '#999',
      },
    })),

    // Email node specific style
    {
      selector: 'node.node-email',
      style: {
        'background-color': '#FF9800',
        'shape': 'round-rectangle',
        'width': 100,
        'height': 45,
        'border-style': 'solid',
        'border-width': 2,
        'border-color': '#F57C00',
      },
    },

    // Knowledge node specific style
    {
      selector: 'node.node-knowledge',
      style: {
        'background-color': '#4DC4D2',
        'shape': 'diamond',
        'width': 80,
        'height': 80,
        'border-style': 'solid',
        'border-width': 2,
        'border-color': '#00ACC1',
      },
    },

    // Conversation node specific style
    {
      selector: 'node.node-conversation',
      style: {
        'background-color': '#325BA8',
        'shape': 'round-octagon',
        'width': 90,
        'height': 70,
        'border-style': 'solid',
        'border-width': 3,
        'border-color': '#1E3A8A',
      },
    },

    // Cluster node styles (compound nodes for grouping)
    {
      selector: 'node.cluster-node',
      style: {
        'background-color': '#E0E0E0',
        'background-opacity': 0.3,
        'shape': 'round-rectangle',
        'border-width': 3,
        'border-color': '#666666',
        'border-style': 'dashed',
        'label': 'data(label)',
        'text-valign': 'top',
        'text-halign': 'center',
        'text-margin-y': -10,
        'font-size': '14px',
        'font-weight': 'bold',
        'color': '#333333',
        'padding': '15px',
        'compound-sizing-wrt-labels': 'include',
        'z-index': 5,
      },
    },

    // Expanded cluster style (when containing child nodes)
    {
      selector: 'node.cluster-expanded',
      style: {
        'background-opacity': 0.1,
        'border-color': '#4056A6',
        'border-width': 2,
      },
    },

    // Collapsed cluster style (clickable to expand)
    {
      selector: 'node.cluster-node:selected',
      style: {
        'background-color': '#B0B0B0',
        'background-opacity': 0.5,
        'border-color': '#4056A6',
        'border-width': 4,
      },
    },

    // Hover effects
    {
      selector: 'node.hover',
      style: {
        'border-width': 4,
        'border-color': '#4056A6',
        'background-opacity': 1,
        'z-index': 20,
      },
    },
    {
      selector: 'edge.hover-connected',
      style: {
        'line-color': '#4056A6',
        'target-arrow-color': '#4056A6',
        'width': 3,
        'z-index': 20,
      },
    },
    {
      selector: 'node.hover-neighbor',
      style: {
        'background-opacity': 1,
        'border-width': 3,
        'z-index': 15,
      },
    },

    // Selected state
    {
      selector: 'node.selected',
      style: {
        'border-width': 5,
        'border-color': '#E94257',
        'background-opacity': 1,
        'z-index': 30,
        'overlay-color': '#E94257',
        'overlay-opacity': 0.2,
      },
    },
    {
      selector: 'edge.selected',
      style: {
        'line-color': '#E94257',
        'target-arrow-color': '#E94257',
        'width': 4,
        'z-index': 30,
      },
    },

    // Search match highlighting
    {
      selector: 'node.search-match',
      style: {
        'background-color': '#FFD700',
        'border-color': '#FFA500',
        'border-width': 4,
        'z-index': 25,
      },
    },

    // Annotation indicator on nodes
    {
      selector: 'node.has-annotations',
      style: {
        'border-width': 3,
        'border-color': '#E94257',  // Coral Red
        'border-style': 'double',
        'overlay-color': '#E94257',
        'overlay-opacity': 0.1,
      },
    },

    // Semantic relationship edges: SUPPORTS (solid turquoise)
    {
      selector: 'edge[type="SUPPORTS"]',
      style: {
        'line-color': '#4DC4D2',
        'target-arrow-color': '#4DC4D2',
        'line-style': 'solid',
        'width': 2,
        'target-arrow-shape': 'triangle',
      },
    },
    // Semantic relationship edges: CONTRADICTS (dashed coral)
    {
      selector: 'edge[type="CONTRADICTS"]',
      style: {
        'line-color': '#E94257',
        'target-arrow-color': '#E94257',
        'line-style': 'dashed',
        'width': 2,
        'target-arrow-shape': 'triangle',
      },
    },
    // Semantic relationship edges: CAUSES (solid deep blue)
    {
      selector: 'edge[type="CAUSES"]',
      style: {
        'line-color': '#325BA8',
        'target-arrow-color': '#325BA8',
        'line-style': 'solid',
        'width': 2,
        'target-arrow-shape': 'triangle',
      },
    },
    // Semantic relationship edges: DEPENDS_ON (dotted purple)
    {
      selector: 'edge[type="DEPENDS_ON"]',
      style: {
        'line-color': '#892987',
        'target-arrow-color': '#892987',
        'line-style': 'dotted',
        'width': 2,
        'target-arrow-shape': 'triangle',
      },
    },
    // Semantic relationship edges: RELATES_TO (default gray)
    {
      selector: 'edge[type="RELATES_TO"]',
      style: {
        'line-color': '#999999',
        'target-arrow-color': '#999999',
        'line-style': 'solid',
        'width': 1,
        'target-arrow-shape': 'triangle',
      },
    },
    // Low confidence semantic edges (dimmed)
    {
      selector: 'edge.low-confidence',
      style: {
        'opacity': 0.4,
      },
    },

    // Temporal old nodes (dimmed appearance for aged nodes)
    {
      selector: 'node.temporal-old',
      style: {
        'background-opacity': 0.35,
        'border-opacity': 0.5,
      },
    },

    // Temporal new nodes (vibrant appearance for recent nodes)
    {
      selector: 'node.temporal-new',
      style: {
        'background-opacity': 1,
        'border-width': 3,
      },
    },

    // Animation: appearing state (start of fade-in)
    {
      selector: 'node.appearing',
      style: {
        'opacity': 0,
      },
    },

    // Animation: disappearing state (during fade-out)
    {
      selector: 'node.disappearing',
      style: {
        'opacity': 0.3,
      },
    },

    // Hidden elements
    {
      selector: 'node.hidden',
      style: {
        'display': 'none',
      },
    },
    {
      selector: 'edge.hidden',
      style: {
        'display': 'none',
      },
    },

    // Todo-specific styles (for future implementation)
    {
      selector: 'node[status="completed"]',
      style: {
        'background-opacity': 0.5,
        'text-decoration': 'line-through',
      },
    },
    {
      selector: 'node[status="overdue"]',
      style: {
        'border-color': '#FF0000',
        'border-width': 3,
        'border-style': 'double',
      },
    },
    {
      selector: 'node[priority="high"]',
      style: {
        'overlay-color': '#FF0000',
        'overlay-opacity': 0.1,
      },
    },
    {
      selector: 'node[priority="critical"]',
      style: {
        'overlay-color': '#FF0000',
        'overlay-opacity': 0.2,
        'border-color': '#FF0000',
      },
    },

    // Compound nodes (for grouping)
    {
      selector: ':parent',
      style: {
        'background-opacity': 0.1,
        'background-color': '#999',
        'border-width': 1,
        'border-color': '#666',
        'border-style': 'dashed',
        'padding': 20,
        'text-valign': 'top',
        'text-halign': 'center',
      },
    },
  ] as any,

  // Layout configurations
  layouts: {
    fcose: {
      name: 'fcose',
      quality: 'proof',
      randomize: false,
      animate: true,
      animationDuration: 1000,
      animationEasing: 'ease-out',
      fit: true,
      padding: 50,
      nodeDimensionsIncludeLabels: true,
      idealEdgeLength: 400,
      edgeElasticity: 0.45,
      nestingFactor: 0.1,
      gravity: 0.15,
      numIter: 2500,
      tile: true,
      tilingPaddingVertical: 60,
      tilingPaddingHorizontal: 60,
      nodeRepulsion: 16000,
      nodeOverlap: 40,
      improveFlow: true,
    },
    circle: {
      name: 'circle',
      animate: true,
      animationDuration: 1000,
      fit: true,
      padding: 50,
      radius: undefined,
      startAngle: 3 / 2 * Math.PI,
      sweep: undefined,
      clockwise: true,
      sort: undefined,
    },
    grid: {
      name: 'grid',
      animate: true,
      animationDuration: 1000,
      fit: true,
      padding: 50,
      rows: undefined,
      cols: undefined,
      position: (node: any) => ({ row: 0, col: 0 }),
    },
    breadthfirst: {
      name: 'breadthfirst',
      animate: true,
      animationDuration: 1000,
      fit: true,
      padding: 50,
      directed: false,
      circle: false,
      grid: false,
      spacingFactor: 1.75,
      maximal: false,
    },
    concentric: {
      name: 'concentric',
      animate: true,
      animationDuration: 1000,
      fit: true,
      padding: 50,
      minNodeSpacing: 10,
      levelWidth: (nodes: any) => 2,
      concentric: (node: any) => node.degree(),
      equidistant: false,
      startAngle: 3 / 2 * Math.PI,
      sweep: undefined,
      clockwise: true,
    },
  },
};

export default cytoscapeConfig;